import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { AlertCircle, CreditCard, Landmark, Loader2, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { stripePromise, stripePublishableKey } from "@/lib/stripe";
import {
  createPayerSetupIntent,
  deletePayerProfile,
  finalizePayerSetup,
  formatPayerMethodSummary,
  type PayerProfileSetup,
  type PayerProfile,
} from "@/services/stripe-payer-profile.service";

const stripeSetupMockEnabled = import.meta.env.VITE_STRIPE_E2E_MOCK === "true";

function getStatusLabel(status: PayerProfile["status"] | null | undefined): string {
  if (status === "active") return "Active";
  if (status === "pendingSetup") return "Pending setup";
  if (status === "requiresAction") return "Requires action";
  if (status === "setupFailed") return "Setup failed";
  if (status === "disabled") return "Disabled";
  return "Not configured";
}

function MethodIcon({ profile }: { profile: PayerProfile | null }) {
  if (profile?.paymentMethodType === "us_bank_account") {
    return <Landmark className="h-5 w-5 text-muted-foreground" aria-hidden="true" />;
  }

  return <CreditCard className="h-5 w-5 text-muted-foreground" aria-hidden="true" />;
}

function ConsentCheckbox({
  checked,
  disabled,
  error,
  onCheckedChange,
}: {
  checked: boolean;
  disabled: boolean;
  error: string | null;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3 rounded-md border p-3">
        <Checkbox
          id="payer-profile-consent"
          data-testid="payer-profile-consent-checkbox"
          checked={checked}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          onCheckedChange={(value) => onCheckedChange(value === true)}
          className="mt-0.5"
        />
        <label htmlFor="payer-profile-consent" className="text-sm leading-5 text-foreground">
          I authorize Shyne to save this payment method with Stripe for future off-session vendor payments. I understand no
          automatic payment will be initiated by this setup.
        </label>
      </div>
      {error ? (
        <p data-testid="payer-profile-consent-error" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function StripeSetupForm({
  setupIntentId,
  consentAccepted,
  consentError,
  submitting,
  onCancel,
  onConsentChange,
  onConsentError,
  onFinalize,
}: {
  setupIntentId: string;
  consentAccepted: boolean;
  consentError: string | null;
  submitting: boolean;
  onCancel: () => void;
  onConsentChange: (checked: boolean) => void;
  onConsentError: (error: string | null) => void;
  onFinalize: (setupIntentId: string) => Promise<void>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const busy = submitting || confirming;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStripeError(null);

    if (!consentAccepted) {
      onConsentError("Accept the authorization before saving this payer profile.");
      return;
    }

    if (!stripe || !elements || !paymentElementReady) {
      setStripeError("Stripe is still loading. Please try again in a moment.");
      return;
    }

    setConfirming(true);

    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (result.error) {
        setStripeError(result.error.message ?? "Unable to save this payment method.");
        return;
      }

      const confirmedSetupIntentId = result.setupIntent?.id ?? setupIntentId;
      if (result.setupIntent?.status !== "succeeded") {
        setStripeError(`Setup status: ${result.setupIntent?.status ?? "unknown"}`);
        return;
      }

      await onFinalize(confirmedSetupIntentId);
    } catch (error: any) {
      setStripeError(error?.message ?? "Unable to save this payment method.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{ layout: { type: "tabs", defaultCollapsed: false } }}
        onReady={() => setPaymentElementReady(true)}
      />
      <ConsentCheckbox
        checked={consentAccepted}
        disabled={busy}
        error={consentError}
        onCheckedChange={onConsentChange}
      />
      {stripeError ? <p className="text-sm text-destructive">{stripeError}</p> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy || !stripe || !elements || !paymentElementReady}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
          {busy ? "Saving..." : "Save payer profile"}
        </Button>
      </div>
    </form>
  );
}

function MockSetupForm({
  setupIntentId,
  consentAccepted,
  consentError,
  submitting,
  onCancel,
  onConsentChange,
  onConsentError,
  onFinalize,
}: {
  setupIntentId: string;
  consentAccepted: boolean;
  consentError: string | null;
  submitting: boolean;
  onCancel: () => void;
  onConsentChange: (checked: boolean) => void;
  onConsentError: (error: string | null) => void;
  onFinalize: (setupIntentId: string) => Promise<void>;
}) {
  const handleSave = async () => {
    if (!consentAccepted) {
      onConsentError("Accept the authorization before saving this payer profile.");
      return;
    }

    await onFinalize(setupIntentId);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
        Stripe setup adapter enabled for E2E.
      </div>
      <ConsentCheckbox
        checked={consentAccepted}
        disabled={submitting}
        error={consentError}
        onCheckedChange={onConsentChange}
      />
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="button" data-testid="payer-profile-e2e-save" disabled={submitting} onClick={handleSave}>
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
          {submitting ? "Saving..." : "Save payer profile"}
        </Button>
      </div>
    </div>
  );
}

function PayerProfileSetupDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (profile: PayerProfile) => void;
}) {
  const [setupState, setSetupState] = useState<PayerProfileSetup | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  const resetSetupState = useCallback(() => {
    setSetupState(null);
    setErrorMessage(null);
    setConsentAccepted(false);
    setConsentError(null);
    setSubmitting(false);
    setLoading(false);
  }, []);

  const loadSetupIntent = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const setupIntent = await createPayerSetupIntent();
      setSetupState(setupIntent);
    } catch (error: any) {
      setErrorMessage(error?.message ?? "Unable to start payer profile setup.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      resetSetupState();
      return;
    }

    void loadSetupIntent();
  }, [loadSetupIntent, open, resetSetupState]);

  const handleConsentChange = (checked: boolean) => {
    setConsentAccepted(checked);
    if (checked) setConsentError(null);
  };

  const handleFinalize = async (setupIntentId: string) => {
    if (!consentAccepted) {
      setConsentError("Accept the authorization before saving this payer profile.");
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try {
      const profile = await finalizePayerSetup(setupIntentId);
      onSaved(profile);
      toast.success("Payer profile saved.");
      onOpenChange(false);
    } catch (error: any) {
      setErrorMessage(error?.message ?? "Unable to save payer profile.");
    } finally {
      setSubmitting(false);
    }
  };

  const canRenderStripeElements = Boolean(setupState?.clientSecret && stripePromise && stripePublishableKey);
  const showMissingStripeConfig = !stripeSetupMockEnabled && setupState?.clientSecret && !canRenderStripeElements;

  return (
    <div
      data-state={open ? "open" : "closed"}
      className={open ? "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" : "hidden"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="payer-profile-dialog-title"
    >
      <div className="w-full max-w-lg rounded-md bg-background p-6 shadow-lg">
        <div className="space-y-2">
          <h2 id="payer-profile-dialog-title" className="text-lg font-semibold">
            Set Up Payer Profile
          </h2>
          <p className="text-sm text-muted-foreground">Save a card or bank account for future vendor payments.</p>
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading Stripe setup...
            </div>
          ) : null}

          {errorMessage ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          ) : null}

          {showMissingStripeConfig ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              Stripe setup is not configured for this environment.
            </div>
          ) : null}

          {!loading && !errorMessage && setupState ? (
            stripeSetupMockEnabled ? (
              <MockSetupForm
                setupIntentId={setupState.setupIntent}
                consentAccepted={consentAccepted}
                consentError={consentError}
                submitting={submitting}
                onCancel={() => onOpenChange(false)}
                onConsentChange={handleConsentChange}
                onConsentError={setConsentError}
                onFinalize={handleFinalize}
              />
            ) : canRenderStripeElements ? (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: setupState.clientSecret,
                  appearance: {
                    theme: "stripe",
                  },
                }}
                key={setupState.clientSecret}
              >
                <StripeSetupForm
                  setupIntentId={setupState.setupIntent}
                  consentAccepted={consentAccepted}
                  consentError={consentError}
                  submitting={submitting}
                  onCancel={() => onOpenChange(false)}
                  onConsentChange={handleConsentChange}
                  onConsentError={setConsentError}
                  onFinalize={handleFinalize}
                />
              </Elements>
            ) : null
          ) : null}
        </div>

        {!setupState || errorMessage || showMissingStripeConfig ? (
          <div className="mt-5 flex justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Close
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface PayerProfilePanelProps {
  profile: PayerProfile | null;
  loading: boolean;
  loadError: string | null;
  onRefresh: () => void | Promise<void>;
  onProfileChange: (profile: PayerProfile | null) => void;
}

export default function PayerProfilePanel({
  profile,
  loading,
  loadError,
  onRefresh,
  onProfileChange,
}: PayerProfilePanelProps) {
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  const errorMessage = loadError ?? actionError;

  const handleRemoveProfile = useCallback(async () => {
    if (!profile) return;

    setDeleting(true);
    setActionError(null);
    try {
      await deletePayerProfile();
      onProfileChange(null);
      setRemoveOpen(false);
      toast.success("Payer profile removed.");
    } catch (error: any) {
      const message = error?.message ?? "Unable to remove payer profile.";
      setActionError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  }, [onProfileChange, profile]);

  const handleProfileSaved = useCallback(
    (savedProfile: PayerProfile) => {
      setActionError(null);
      onProfileChange(savedProfile);
    },
    [onProfileChange]
  );

  const methodSummary = useMemo(() => formatPayerMethodSummary(profile), [profile]);
  const isActive = profile?.status === "active";
  const actionLabel = profile ? "Update payer profile" : "Set up payer profile";

  return (
    <section data-testid="payer-profile-panel" className="rounded-md border bg-background p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
            <MethodIcon profile={profile} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold">Payer Profile</h2>
              <Badge variant={isActive ? "default" : "outline"}>{getStatusLabel(profile?.status)}</Badge>
            </div>
            {loading ? (
              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading payer profile...
              </div>
            ) : errorMessage ? (
              <div className="mt-2 flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                {errorMessage}
              </div>
            ) : profile ? (
              <p data-testid="payer-profile-summary" className="mt-1 text-sm text-muted-foreground">
                {methodSummary}
              </p>
            ) : (
              <p data-testid="payer-profile-empty" className="mt-1 text-sm text-muted-foreground">
                No payer profile saved.
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => void onRefresh()}
            disabled={loading}
            aria-label="Refresh payer profile"
          >
            <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          </Button>
          {profile ? (
            <Button
              type="button"
              variant="outline"
              data-testid="payer-profile-remove-button"
              onClick={() => setRemoveOpen(true)}
              disabled={loading || deleting}
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Remove
            </Button>
          ) : null}
          <Button
            type="button"
            data-testid="payer-profile-setup-button"
            onClick={() => setSetupOpen(true)}
            disabled={loading || deleting}
          >
            <ShieldCheck className="mr-2 h-4 w-4" />
            {actionLabel}
          </Button>
        </div>
      </div>

      <PayerProfileSetupDialog open={setupOpen} onOpenChange={setSetupOpen} onSaved={handleProfileSaved} />
      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove payer profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the saved payment method from future vendor payments. You can set up a new payer profile later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="payer-profile-confirm-remove"
              disabled={deleting}
              onClick={(event) => {
                event.preventDefault();
                void handleRemoveProfile();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
